/**
 * @fileoverview Card de pré-visualização de localização compartilhada na conversa.
 * Renderização leve (sem mapa JS): gradiente com pattern + pin. Clicar abre Google Maps.
 */

import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { MapPin } from "lucide-react";
// Extrai lat/long do link "https://maps.google.com/maps?q=LAT%2CLONG&..."
const parseCoordsFromLink = (link) => {
  if (!link) return null;
  try {
    const url = new URL(link);
    const q = url.searchParams.get("q");
    if (!q) return null;
    const [latRaw, lonRaw] = q.split(",").map((s) => decodeURIComponent(s).trim());
    const lat = parseFloat(latRaw);
    const lon = parseFloat(lonRaw);
    if (Number.isFinite(lat) && Number.isFinite(lon)) return { lat, lon };
  } catch (_) {}
  return null;
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    card: {
      width: 280,
      borderRadius: 14,
      overflow: "hidden",
      cursor: "pointer",
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.08)"
        : "1px solid rgba(15, 23, 42, 0.08)",
      background: dark ? "rgba(255, 255, 255, 0.02)" : "#ffffff",
      transition: "transform 120ms ease, box-shadow 120ms ease",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: dark
          ? "0 8px 24px rgba(0, 0, 0, 0.35)"
          : "0 8px 24px rgba(15, 23, 42, 0.08)",
      },
    },
    mapBox: {
      position: "relative",
      width: "100%",
      height: 140,
      background: dark
        ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
        : "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)",
      // "Map-ish" pattern — linhas sutis simulando estradas/grid.
      backgroundImage: dark
        ? `linear-gradient(135deg, #1e293b 0%, #0f172a 100%),
           repeating-linear-gradient(45deg, transparent 0, transparent 22px, rgba(148,163,184,0.08) 22px, rgba(148,163,184,0.08) 23px),
           repeating-linear-gradient(-45deg, transparent 0, transparent 18px, rgba(148,163,184,0.06) 18px, rgba(148,163,184,0.06) 19px)`
        : `linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%),
           repeating-linear-gradient(45deg, transparent 0, transparent 22px, rgba(59,130,246,0.08) 22px, rgba(59,130,246,0.08) 23px),
           repeating-linear-gradient(-45deg, transparent 0, transparent 18px, rgba(59,130,246,0.06) 18px, rgba(59,130,246,0.06) 19px)`,
      backgroundBlendMode: "normal, overlay, overlay",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    pin: {
      fontSize: 54,
      color: "#ef4444",
      filter: "drop-shadow(0 3px 6px rgba(0, 0, 0, 0.35))",
    },
    footer: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 12px",
    },
    pinChip: {
      width: 32,
      height: 32,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      background: dark ? "rgba(239, 68, 68, 0.14)" : "rgba(239, 68, 68, 0.10)",
      color: "#ef4444",
    },
    texts: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    title: {
      fontSize: 13,
      fontWeight: 600,
      color: dark ? "#e2e8f0" : "#111827",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    coords: {
      fontSize: 11,
      color: dark ? "rgba(226, 232, 240, 0.55)" : "#6b7280",
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  };
});

const LocationPreview = ({ image, link, description }) => {
  const classes = useStyles();

  const coords = parseCoordsFromLink(link);
  const hasCoords = !!coords;
  const hasThumbnail = image && image.includes("data:image");

  const handleClick = () => {
    if (link) window.open(link, "_blank", "noopener,noreferrer");
  };

  const cleanDescription = (description || "").trim();
  const displayTitle = cleanDescription || "Localização";
  const coordsLabel = hasCoords
    ? `${coords.lat.toFixed(5)}, ${coords.lon.toFixed(5)}`
    : "";

  return (
    <div className={classes.card} onClick={handleClick} role="button" tabIndex={0}>
      <div className={classes.mapBox}>
        {hasThumbnail ? (
          <img
            src={image}
            alt="localização"
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <MapPin className={classes.pin} />
        )}
      </div>

      <div className={classes.footer}>
        <div className={classes.pinChip}>
          <MapPin size={18} />
        </div>
        <div className={classes.texts}>
          <span className={classes.title}>{displayTitle}</span>
          {coordsLabel && <span className={classes.coords}>{coordsLabel}</span>}
        </div>
      </div>
    </div>
  );
};

export default LocationPreview;
